/* eslint-disable react/no-danger */
import "./style.css";
import lod_ from "lodash";
import { Avatar, Box, Icon, IconButton, Popover, TextField, Tooltip } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";
import TestingQuestionDialog from "components/Custom/TestingQuestionDialog/TestingQuestionDialog";
import FaiqActions from "redux-react/actions/faiqActions";
import { display } from "redux-react/reducers/snackBarReducer";

/**
 * Sources displayed as a grid of clickable cards under an assistant message.
 * Each card opens the source URL in a new tab.
 */
const SourceCards = ({ sources }) => {
	if (!sources?.length) return null;

	return (
		<MDBox mt={2} className="faiq-source-cards">
			<MDTypography variant="caption" className="faiq-source-cards-title">
				{sources.length} {t(sources.length > 1 ? "FAIQ.CHAT.sources" : "FAIQ.CHAT.source")}
			</MDTypography>
			<MDBox className="faiq-source-grid">
				{sources.map((source, index) => {
					const url = source?.url || source?.metadatas?.url;
					const title = source?.title || source?.metadatas?.title || "Source";
					const description = source?.description || source?.metadatas?.description || "";
					const image = source?.image || source?.metadatas?.image;
					const initial = (title || description || "S").trim()[0]?.toUpperCase();

					return (
						<a
							key={`source-${index}`}
							href={url}
							target="_blank"
							rel="noreferrer"
							className="faiq-source-card"
						>
							<div className="faiq-source-card-avatar">
								<Avatar
									src={image}
									alt={title}
									sx={{ width: 32, height: 32 }}
									imgProps={{
										style: {
											objectFit: "cover"
										}
									}}
								>
									{initial}
								</Avatar>
							</div>
							<div className="faiq-source-card-body">
								<div className="faiq-source-card-title" title={title}>
									{title}
								</div>
								{description && (
									<div className="faiq-source-card-desc" title={description}>
										{description}
									</div>
								)}
							</div>
							<Icon className="faiq-source-card-arrow" fontSize="small">
								open_in_new
							</Icon>
						</a>
					);
				})}
			</MDBox>
		</MDBox>
	);
};

const FEEDBACK_TYPES = {
	positive: "positive",
	negative: "negative"
};

/**
 * Thumbs up/down with optional comment popover.
 * Sends feedback through redux action; locks state once submitted but allows
 * the user to switch their vote.
 */
const FeedbackButtons = ({ questionID, threadID, assistantID }) => {
	const dispatch = useDispatch();
	const [vote, setVote] = useState(null);
	const [anchor, setAnchor] = useState(null);
	const [pendingFeedback, setPendingFeedback] = useState(null);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);

	if (!questionID || !assistantID) return null;

	const open = Boolean(anchor);

	const openPopover = (event, feedback) => {
		setPendingFeedback(feedback);
		setAnchor(event.currentTarget);
	};

	const closePopover = () => {
		setAnchor(null);
		setPendingFeedback(null);
		setComment("");
	};

	const submit = (feedback, comment) => {
		setSubmitting(true);

		dispatch(
			FaiqActions.sendFeedback(
				{
					questionID,
					feedback
				},
				() => {
					setVote(feedback);
					setSubmitting(false);
					dispatch(
						display({
							type: "success",
							message: t("FAIQ.CHAT.feedbackSent")
						})
					);
					closePopover();
				},
				() => {
					setSubmitting(false);
				}
			)
		);

		if (feedback === "negative" && !lod_.isEmpty(comment.trim())) {
			dispatch(
				FaiqActions.sendReview(
					{
						threadID,
						rating: 1,
						content: comment.trim()
					},
					() => {},
					() => {}
				)
			);
		}
	};

	return (
		<>
			<Tooltip placement="top" title={t("FAIQ.CHAT.feedbackPositive")}>
				<IconButton
					size="small"
					className={`faiq-feedback-btn ${vote === "positive" ? "active positive" : ""}`}
					onClick={event => submit(FEEDBACK_TYPES.positive)}
					disabled={submitting}
				>
					<Icon fontSize="small">{vote === "positive" ? "thumb_up_alt" : "thumb_up_off_alt"}</Icon>
				</IconButton>
			</Tooltip>
			<Tooltip placement="top" title={t("FAIQ.CHAT.feedbackNegative")}>
				<IconButton
					size="small"
					className={`faiq-feedback-btn ${vote === "negative" ? "active negative" : ""}`}
					onClick={event => openPopover(event, FEEDBACK_TYPES.negative)}
					disabled={submitting}
				>
					<Icon fontSize="small">
						{vote === "negative" ? "thumb_down_alt" : "thumb_down_off_alt"}
					</Icon>
				</IconButton>
			</Tooltip>
			<Popover
				open={open}
				anchorEl={anchor}
				onClose={closePopover}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{
					paper: { className: "faiq-feedback-popover" }
				}}
			>
				<MDBox p={2} sx={{ width: 320 }}>
					<MDTypography variant="button" fontWeight="medium">
						{t("FAIQ.CHAT.feedbackNegativeTitle")}
					</MDTypography>
					<MDTypography variant="caption" color="text" display="block" sx={{ mt: 0.5 }}>
						{t("FAIQ.CHAT.feedbackOptional")}
					</MDTypography>
					<TextField
						fullWidth
						multiline
						minRows={3}
						maxRows={6}
						placeholder={t("FAIQ.CHAT.feedbackPlaceholder")}
						value={comment}
						onChange={e => setComment(e.target.value)}
						sx={{ mt: 1.5 }}
						autoFocus
					/>
					<MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
						<MDButton
							size="small"
							variant="contained"
							color="info"
							onClick={() => submit(pendingFeedback, comment)}
							disabled={submitting}
						>
							{t("FAIQ.CHAT.send")}
						</MDButton>
					</MDBox>
				</MDBox>
			</Popover>
		</>
	);
};

/**
 * Maps a backend phase token to a human-readable label.
 * @param {string} phase - phase token (analysis | kb_search | writing)
 */
const phaseLabel = phase => {
	switch (phase) {
		case "analysis":
			return t("FAIQ.CHAT.PHASES.analysis");
		case "kb_search":
			return t("FAIQ.CHAT.PHASES.kb_search");
		case "writing":
			return t("FAIQ.CHAT.PHASES.writing");
		default:
			return t("FAIQ.CHAT.PHASES.default");
	}
};

/**
 * Typing indicator (assistant is processing / writing).
 * Shows animated dots and, when available, the current processing phase.
 * @param {object} props
 * @param {string} [props.phase] - current backend phase token
 */
const TypingIndicator = ({ phase }) => (
	<MDBox className="faiq-typing-wrap">
		<MDBox className="faiq-typing">
			<span className="faiq-typing-dot"></span>
			<span className="faiq-typing-dot"></span>
			<span className="faiq-typing-dot"></span>
		</MDBox>
		<span className="faiq-typing-label">{phaseLabel(phase)}</span>
	</MDBox>
);

const MULTI_MESSAGE_VIEW_MODES = {
	list: "list",
	carousel: "carousel"
};

const sanitizeSources = sources => {
	if (!Array.isArray(sources)) return [];
	return sources.filter(source => source?.metadatas?.url || source?.url);
};

const AssistantMultiMessage = ({ items = [] }) => {
	const [viewMode, setViewMode] = useState(MULTI_MESSAGE_VIEW_MODES.list);
	const [activeIndex, setActiveIndex] = useState(0);

	if (!items.length) return null;

	const safeIndex = Math.min(activeIndex, items.length - 1);
	const currentItem = items[safeIndex] || { message: "", sources: [] };
	const hasMultipleItems = items.length > 1;

	const showPrevious = () => {
		setActiveIndex(prev => (prev - 1 + items.length) % items.length);
	};

	const showNext = () => {
		setActiveIndex(prev => (prev + 1) % items.length);
	};

	return (
		<MDBox className="faiq-multi-message-wrap">
			{hasMultipleItems && (
				<div className="faiq-multi-message-header">
					<span className="faiq-multi-message-title">{t("FAIQ.CHAT.multiDisplay")}</span>
					<div className="faiq-multi-message-switch" role="radiogroup">
						<button
							type="button"
							role="radio"
							aria-checked={viewMode === MULTI_MESSAGE_VIEW_MODES.list}
							className={`faiq-multi-mode-btn ${viewMode === MULTI_MESSAGE_VIEW_MODES.list ? "active" : ""}`}
							onClick={() => setViewMode(MULTI_MESSAGE_VIEW_MODES.list)}
						>
							{t("FAIQ.CHAT.displayList")}
						</button>
						<button
							type="button"
							role="radio"
							aria-checked={viewMode === MULTI_MESSAGE_VIEW_MODES.carousel}
							className={`faiq-multi-mode-btn ${viewMode === MULTI_MESSAGE_VIEW_MODES.carousel ? "active" : ""}`}
							onClick={() => setViewMode(MULTI_MESSAGE_VIEW_MODES.carousel)}
						>
							{t("FAIQ.CHAT.displayCarousel")}
						</button>
					</div>
				</div>
			)}

			{viewMode === MULTI_MESSAGE_VIEW_MODES.list ? (
				<MDBox className="faiq-multi-message-list">
					{items.map((item, index) => (
						<MDBox
							key={item.id || `answer-${index}`}
							className={`faiq-multi-message-item ${index > 0 ? "with-separator" : ""}`}
						>
							{hasMultipleItems && (
								<div className="faiq-multi-message-index">
									{t("FAIQ.CHAT.answerIndex", { index: index + 1, count: items.length })}
								</div>
							)}
							<div
								className="faiq-msg-text"
								dangerouslySetInnerHTML={{ __html: item.message || "" }}
							/>
							<SourceCards sources={sanitizeSources(item.sources)} />
						</MDBox>
					))}
				</MDBox>
			) : (
				<MDBox className="faiq-multi-carousel">
					<IconButton
						size="small"
						onClick={showPrevious}
						className="faiq-multi-carousel-nav"
						disabled={!hasMultipleItems}
					>
						<Icon fontSize="small">chevron_left</Icon>
					</IconButton>
					<MDBox className="faiq-multi-carousel-body">
						<div className="faiq-multi-message-index">
							{t("FAIQ.CHAT.answerIndex", { index: safeIndex + 1, count: items.length })}
						</div>
						<div
							className="faiq-msg-text"
							dangerouslySetInnerHTML={{ __html: currentItem.message || "" }}
						/>
						<SourceCards sources={sanitizeSources(currentItem.sources)} />
					</MDBox>
					<IconButton
						size="small"
						onClick={showNext}
						className="faiq-multi-carousel-nav"
						disabled={!hasMultipleItems}
					>
						<Icon fontSize="small">chevron_right</Icon>
					</IconButton>
				</MDBox>
			)}
		</MDBox>
	);
};

/**
 * Single chat message — user (right bubble) or assistant (full width).
 */
const Message = ({
	id,
	threadID,
	message,
	messageItems = [],
	sources = [],
	direction,
	loading = false,
	phase = null,
	error = false,
	assistantID
}) => {
	const dispatch = useDispatch();
	const permissions = useSelector(state => state.permissions?.permissions);
	const [copied, setCopied] = useState(false);
	const [testingQuestionDialog, setTestingQuestionDialog] = useState({ open: false });

	const isUser = direction === "out";
	const safeMessageItems = Array.isArray(messageItems) ? messageItems : [];
	const hasMultipleMessages = safeMessageItems.length > 0;

	const closeTesting = () => setTestingQuestionDialog({ open: false });

	const htmlToText = html => {
		const tmp = document.createElement("div");
		tmp.innerHTML = html || "";
		return tmp.innerText || tmp.textContent || "";
	};

	const copyMessage = async () => {
		try {
			const text = hasMultipleMessages
				? safeMessageItems
						.map(item => htmlToText(item?.message))
						.filter(Boolean)
						.join("\n\n")
				: htmlToText(message);
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch (e) {
			dispatch(
				display({
					type: "error",
					message: t("FAIQ.CHAT.copyError")
				})
			);
		}
	};

	// User bubble
	if (isUser) {
		return (
			<MDBox className="faiq-msg-row user">
				<MDBox className="faiq-msg-bubble user">
					<div dangerouslySetInnerHTML={{ __html: message }} />
				</MDBox>
			</MDBox>
		);
	}

	// Assistant message
	let assistantBody = (
		<div className="faiq-msg-text" dangerouslySetInnerHTML={{ __html: message || "" }} />
	);

	if (loading) {
		assistantBody = <TypingIndicator phase={phase} />;
	} else if (hasMultipleMessages) {
		assistantBody = <AssistantMultiMessage items={safeMessageItems} />;
	}

	return (
		<MDBox className={`faiq-msg-row assistant ${error ? "error" : ""}`}>
			<TestingQuestionDialog
				{...testingQuestionDialog}
				questionID={id}
				threadID={threadID}
				open={testingQuestionDialog.open}
				onClose={closeTesting}
			/>
			<Box className={`faiq-msg-avatar ${error ? "error" : ""}`}>
				<Icon className="faiq-msg-avatar-icon">{error ? "error_outline" : "auto_awesome"}</Icon>
			</Box>
			<MDBox className="faiq-msg-content">
				<MDBox className="faiq-msg-card">
					{assistantBody}

					{!loading && !hasMultipleMessages && <SourceCards sources={sanitizeSources(sources)} />}
				</MDBox>

				{!loading && !error && (
					<MDBox className="faiq-msg-actions" mt={1}>
						<Tooltip placement="top" title={copied ? t("FAIQ.CHAT.copied") : t("FAIQ.CHAT.copy")}>
							<IconButton size="small" onClick={copyMessage} className="faiq-msg-action-btn">
								<Icon fontSize="small">{copied ? "check" : "content_copy"}</Icon>
							</IconButton>
						</Tooltip>
						<FeedbackButtons questionID={id} threadID={threadID} assistantID={assistantID} />
						{/* {permissions?.includes("exec:overtrainingFromExchange") && (
							<Tooltip placement="top" title={t("FAIQ.TESTING.overtrainingFromThisMessage")}>
								<IconButton
									size="small"
									className="faiq-msg-action-btn"
									onClick={() => setTestingQuestionDialog({ open: true })}
								>
									<Icon fontSize="small">mode_standby</Icon>
								</IconButton>
							</Tooltip>
						)} */}
					</MDBox>
				)}
			</MDBox>
		</MDBox>
	);
};

export default Message;
